import { Sparkles } from "lucide-react";

/**
 * Iter-147 — Calm, persistent "Scenes & Cast built" indicator.
 *
 * Renders nothing when the script slot has not been parsed yet (or
 * `parsedAt` is null). When the slot has been built, surfaces:
 *
 *   ✓ Scenes & Cast built · 12 scenes · 8 cast · 2 days ago
 *
 * Tone: matches the calm/invisible UI philosophy — single subline,
 * subtle emerald tint on the checkmark only, no badge or button.
 */
function _relativeTimeAgo(iso) {
    if (!iso) return "";
    const ms = Date.now() - Date.parse(iso);
    if (!Number.isFinite(ms) || ms < 0) return "";
    const s = Math.floor(ms / 1000);
    if (s < 60) return "just now";
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    const mo = Math.floor(d / 30);
    if (mo < 12) return `${mo}mo ago`;
    return `${Math.floor(mo / 12)}y ago`;
}

export default function ScriptParsedBadge({
    parsedAt,
    scenesCount,
    charactersCount,
    testid = "script-parsed-badge",
}) {
    if (!parsedAt) return null;
    const parts = [];
    // Iter-158.13.2.20f.3 #12 — hide zero counts. A `parsed_at`-
    // stamped script row that reports 0 scenes or 0 characters is a
    // data smell (usually a pre-Iter-147 slot that never back-filled
    // parsed_*_count); the aggregate Production counts on the parent
    // surface already tell the truth. Suppressing the misleading
    // "0 characters" here removes the contradiction UAT flagged.
    if (typeof scenesCount === "number" && scenesCount > 0) {
        parts.push(
            `${scenesCount} ${scenesCount === 1 ? "scene" : "scenes"}`,
        );
    }
    if (typeof charactersCount === "number" && charactersCount > 0) {
        parts.push(
            `${charactersCount} ${charactersCount === 1 ? "cast" : "cast"}`,
        );
    }
    const ago = _relativeTimeAgo(parsedAt);
    if (ago) parts.push(ago);
    return (
        <div
            data-testid={testid}
            className="mt-2 flex items-center gap-1.5 text-[11px] text-neutral-400"
        >
            <Sparkles size={11} className="text-emerald-300" />
            <span data-testid={`${testid}-label`}>
                Scenes &amp; Cast built
            </span>
            {parts.length ? (
                <span
                    data-testid={`${testid}-meta`}
                    className="text-neutral-500"
                >
                    · {parts.join(" · ")}
                </span>
            ) : null}
        </div>
    );
}
